import { useCallback, useMemo, useState } from 'react';

import type { RecurringCadence } from '@splitfiesta/shared';
import { todayUtc } from '@splitfiesta/shared';

import {
  type RepeatAvailableArgs,
  repeatAvailable,
  repeatPreview,
} from '../recurring/recurring';

export interface UseRepeatChoiceInput {
  /** Null while the group is unknown: Repeat is not offered. */
  availability: RepeatAvailableArgs | null;
  /** The entry itself (foreign currency, items) rules Repeat out. */
  blockedByContent: boolean;
  /** The date of the expense being saved, YYYY-MM-DD. */
  anchorDate: string;
  /** Called on every choice the person makes, e.g. to mark the form dirty. */
  onChange?: () => void;
}

export interface RepeatPayload {
  cadence: RecurringCadence;
  endDate?: string;
}

export interface RepeatChoice {
  available: boolean;
  cadence: RecurringCadence | null;
  endDate: string | null;
  setCadence: (cadence: RecurringCadence | null) => void;
  setEndDate: (endDate: string | null) => void;
  /** Show the "plain expenses only" note in place of the section. */
  plainOnly: boolean;
  preview: { nextDate: string; endsBefore: boolean } | null;
  /** Spread into the create body; empty when nothing would repeat. */
  payload: () => { repeat?: RepeatPayload };
}

/** The Repeat section of the add-expense form, shared by web and mobile. */
export function useRepeatChoice({
  availability,
  blockedByContent,
  anchorDate,
  onChange,
}: UseRepeatChoiceInput): RepeatChoice {
  const [cadence, setCadenceState] = useState<RecurringCadence | null>(null);
  const [endDate, setEndDateState] = useState<string | null>(null);
  const [plainOnly, setPlainOnly] = useState(false);

  const available = availability !== null && repeatAvailable(availability);

  // Losing the section drops the choice; the note says why only when the
  // person's own entry (currency, items) caused it.
  if (!available && cadence !== null) {
    setCadenceState(null);
    setEndDateState(null);
    if (blockedByContent) setPlainOnly(true);
  }
  if (plainOnly && (available || !blockedByContent)) {
    setPlainOnly(false);
  }

  const setCadence = useCallback(
    (next: RecurringCadence | null) => {
      onChange?.();
      setCadenceState(next);
      if (!next) setEndDateState(null);
    },
    [onChange]
  );
  const setEndDate = useCallback(
    (next: string | null) => {
      onChange?.();
      setEndDateState(next);
    },
    [onChange]
  );

  // UTC, not the device date: the server and the daily run count in UTC
  // dates, so a local date would preview a day the rule never creates.
  const today = todayUtc();
  const preview = useMemo(
    () =>
      cadence
        ? repeatPreview({
            anchorDate,
            cadence,
            endDate: endDate ?? undefined,
            today,
          })
        : null,
    [cadence, endDate, anchorDate, today]
  );

  // A rule that ends before its first new date would be ended at birth;
  // the preview already shows both dates, so nothing is sent.
  const payload = useCallback((): { repeat?: RepeatPayload } => {
    if (!available || !cadence || !preview || preview.endsBefore) return {};
    return { repeat: { cadence, ...(endDate ? { endDate } : {}) } };
  }, [available, cadence, endDate, preview]);

  return {
    available,
    cadence,
    endDate,
    setCadence,
    setEndDate,
    plainOnly,
    preview,
    payload,
  };
}
